import React, { useRef, useState } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Trash2, User, Baby } from 'lucide-react';

/**
 * Slice B.2 — Body Map renderer (adult + child, front/back, click-to-pin).
 * Emits the B.1 backend contract:
 *   { variant: 'adult'|'child', regions: [{ x, y, side, note? }] }
 * Coordinate data only — NO freehand drawing. Max 30 regions, note ≤200 chars.
 */

const MAX_REGIONS = 30;
const NOTE_MAX = 200;
const VB_W = 100;
const VB_H = 220;

function clamp01(n) { return Math.min(1, Math.max(0, n)); }

/** Simple humanoid silhouette — proportions vary by variant. No anatomy detail. */
function Silhouette({ variant }) {
  const child = variant === 'child';
  const headR = child ? 20 : 15;
  const headCy = headR + 6;
  const torsoY = headCy + headR - 2;
  const torsoH = child ? 60 : 78;
  const legY = torsoY + torsoH;
  const legH = child ? 70 : 96;
  const fill = '#e2e8f0';
  const stroke = '#94a3b8';
  return (
    <g fill={fill} stroke={stroke} strokeWidth="1.2">
      {/* head */}
      <circle cx="50" cy={headCy} r={headR} />
      {/* neck */}
      <rect x="45" y={headCy + headR - 4} width="10" height="8" rx="3" />
      {/* torso */}
      <rect x="30" y={torsoY} width="40" height={torsoH} rx="14" />
      {/* arms */}
      <rect x="14" y={torsoY + 2} width="12" height={torsoH - 14} rx="6" />
      <rect x="74" y={torsoY + 2} width="12" height={torsoH - 14} rx="6" />
      {/* legs */}
      <rect x="33" y={legY - 6} width="15" height={legH} rx="7" />
      <rect x="52" y={legY - 6} width="15" height={legH} rx="7" />
    </g>
  );
}

export function BodyMapField({ value, onChange }) {
  const svgRef = useRef(null);
  const [selected, setSelected] = useState(null);
  const v = value && typeof value === 'object' ? value : {};
  const variant = v.variant || 'adult';
  const regions = Array.isArray(v.regions) ? v.regions : [];
  const [side, setSide] = useState('front');

  const commit = (next) => onChange({ variant, regions: next });
  const setVariant = (nv) => onChange({ variant: nv, regions });

  const addPin = (e) => {
    if (regions.length >= MAX_REGIONS) return;
    const rect = svgRef.current.getBoundingClientRect();
    const x = clamp01((e.clientX - rect.left) / rect.width);
    const y = clamp01((e.clientY - rect.top) / rect.height);
    const next = [...regions, { x: Number(x.toFixed(4)), y: Number(y.toFixed(4)), side }];
    commit(next);
    setSelected(next.length - 1);
  };

  const removePin = (idx) => {
    const next = regions.filter((_, i) => i !== idx);
    commit(next);
    setSelected(null);
  };

  const setNote = (idx, note) => {
    const next = regions.map((r, i) => (i === idx ? { ...r, note } : r));
    commit(next);
  };

  const visiblePins = regions
    .map((r, i) => ({ ...r, i }))
    .filter(r => (r.side || 'front') === side);

  return (
    <div className="rounded-md border p-3 space-y-3" data-testid="chart-field-body-map">
      <div className="flex items-center justify-between gap-2">
        <div className="inline-flex rounded-md border overflow-hidden">
          <button
            type="button"
            onClick={() => setVariant('adult')}
            data-testid="chart-field-body-map-variant-adult"
            className={`px-3 py-1.5 text-xs inline-flex items-center gap-1 ${variant === 'adult' ? 'bg-slate-800 text-white' : 'bg-white text-slate-600'}`}
          >
            <User className="w-3.5 h-3.5" /> Adult
          </button>
          <button
            type="button"
            onClick={() => setVariant('child')}
            data-testid="chart-field-body-map-variant-child"
            className={`px-3 py-1.5 text-xs inline-flex items-center gap-1 ${variant === 'child' ? 'bg-slate-800 text-white' : 'bg-white text-slate-600'}`}
          >
            <Baby className="w-3.5 h-3.5" /> Child
          </button>
        </div>
        <div className="inline-flex rounded-md border overflow-hidden">
          {['front', 'back'].map(s => (
            <button
              key={s}
              type="button"
              onClick={() => setSide(s)}
              data-testid={`chart-field-body-map-side-${s}`}
              className={`px-3 py-1.5 text-xs capitalize ${side === s ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600'}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <p className="text-[11px] text-muted-foreground">
        Click the body to drop a marker ({regions.length}/{MAX_REGIONS}). Markers store position only.
      </p>

      <div className="flex justify-center">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${VB_W} ${VB_H}`}
          className="w-[160px] h-[300px] cursor-crosshair touch-none select-none"
          onClick={addPin}
        >
          <Silhouette variant={variant} />
          {visiblePins.map(p => (
            <g key={p.i} onClick={(e) => { e.stopPropagation(); setSelected(p.i); }} className="cursor-pointer">
              <circle
                cx={p.x * VB_W}
                cy={p.y * VB_H}
                r="5.5"
                fill={selected === p.i ? '#dc2626' : '#ef4444'}
                stroke="#fff"
                strokeWidth="1.5"
                data-testid={`chart-field-body-map-pin-${p.i}`}
              />
              <text x={p.x * VB_W} y={p.y * VB_H + 2.6} textAnchor="middle" fontSize="6" fill="#fff" fontWeight="700">
                {p.i + 1}
              </text>
            </g>
          ))}
        </svg>
      </div>

      {visiblePins.length === 0 ? (
        <p className="text-[11px] text-center text-muted-foreground italic">No markers on the {side} view yet.</p>
      ) : (
        <div className="space-y-2">
          {visiblePins.map(p => (
            <div key={p.i} className={`rounded border p-2 ${selected === p.i ? 'border-red-300 bg-red-50/40' : 'border-slate-200'}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] font-medium text-slate-600">
                  #{p.i + 1} · {side} · ({p.x.toFixed(2)}, {p.y.toFixed(2)})
                </span>
                <Button
                  type="button" variant="ghost" size="sm"
                  className="h-6 px-2 text-red-600"
                  onClick={() => removePin(p.i)}
                  data-testid={`chart-field-body-map-remove-${p.i}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
              <Input
                value={p.note || ''}
                maxLength={NOTE_MAX}
                placeholder="Optional note (e.g. bruise, redness)"
                onChange={e => setNote(p.i, e.target.value)}
                className="h-8 text-xs"
                data-testid={`chart-field-body-map-note-${p.i}`}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default BodyMapField;
